import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMenu, FiX, FiUser } from 'react-icons/fi';
import logoImage from '../assets/GMF logo no background.png';
import styles from '../Styles/Header.module.css';
import { useAuth } from '../Components/AuthContext';

const Header = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [useMobileView, setUseMobileView] = useState(false);

    const headerRef = useRef(null);
    const profileMenuRef = useRef(null);
    const desktopNavRef = useRef(null);
    const mobileMenuRef = useRef(null);

    const navigate = useNavigate();
    const { token, role, user, logout } = useAuth();
    const firstName = user?.first_name || user?.institution_name || 'Profile';

    // Navigation links
    const allLinks = [
        {
            key: 'chat',
            label: 'Conversations',
            to: '/chat',
            title: 'View and manage conversations you are currently part of',
            allowedRoles: ['GMF_EMPLOYEE'],
        },
        {
            key: 'aichat',
            label: 'AI Conversation History',
            to: '/hcw-ai-chats',
            title: 'Access and review conversations between healthcare workers and the AI chatbot',
            allowedRoles: ['GMF_EMPLOYEE', 'INSTITUTION'],
        },
        // healthcare workers
        {
            key: 'viewhcws',
            label: 'Healthcare Workers',
            to: '/view-healthcare-workers',
            title: 'View and edit approved healthcare workers in the field',
            allowedRoles: ['GMF_EMPLOYEE', 'INSTITUTION'],
        },
        {
            key: 'viewGMF',
            label: 'GMF Accounts',
            to: '/view-gmf-accounts',
            title: 'View and delete Global Medic Force accounts',
            allowedRoles: ['GMF_EMPLOYEE'],
        },
        {
            key: 'viewinstitutions',
            label: 'Institutions',
            to: '/view-institutions',
            allowedRoles: ['GMF_EMPLOYEE'],
        },
        {
            key: 'approve',
            label: 'Pending Approvals',
            to: '/approve-requests',
            title: 'Review and approve pending account creation requests',
            allowedRoles: ['GMF_EMPLOYEE'],
        },
    ];
    const navLinks = allLinks.filter((link) =>
        link.allowedRoles.includes(role)
    );

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                profileMenuRef.current &&
                !profileMenuRef.current.contains(e.target)
            ) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close mobile dropdown on outside click
    useEffect(() => {
        const handleClickOutsideMobile = (e) => {
            if (
                mobileMenuRef.current &&
                !mobileMenuRef.current.contains(e.target)
            ) {
                setMobileMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutsideMobile);
        return () =>
            document.removeEventListener('mousedown', handleClickOutsideMobile);
    }, []);

    // Resize observer to check if we need to switch to mobile view
    useEffect(() => {
        if (
            !headerRef.current ||
            !profileMenuRef.current ||
            !desktopNavRef.current
        ) {
            console.warn('Missing refs for ResizeObserver');
            return;
        }

        // We'll store the scheduled rAF ID here so we can cancel if needed
        let frameId = null;

        const resizeHandler = () => {
            if (frameId !== null) cancelAnimationFrame(frameId);

            frameId = requestAnimationFrame(() => {
                frameId = null;

                const headerW = headerRef.current?.clientWidth ?? 0;
                const profileW = profileMenuRef.current?.offsetWidth ?? 0;
                const navW = desktopNavRef.current?.scrollWidth ?? 0;
                const buffer = 66 + 20 + 20;

                const shouldUseMobile = navW + profileW + buffer > headerW;
                setUseMobileView((prev) =>
                    prev === shouldUseMobile ? prev : shouldUseMobile
                );
            });
        };

        const observer = new ResizeObserver(resizeHandler);
        observer.observe(headerRef.current);

        return () => {
            observer.disconnect();
            if (frameId !== null) {
                cancelAnimationFrame(frameId);
            }
        };
    }, []);

    return (
        <nav className={styles.headerNav} ref={headerRef}>
            <div className={styles.headerLeft}>
                <Link to="/" title="Go to the homepage dashboard">
                    <img
                        src={logoImage}
                        alt="GMF Logo"
                        className={styles.logoImg}
                    />
                </Link>

                <div
                    ref={desktopNavRef}
                    className={
                        useMobileView
                            ? styles.desktopNavHidden
                            : styles.desktopNav
                    }
                >
                    <ul className={styles.navItems}>
                        {navLinks.map((link) => (
                            <li key={link.key}>
                                <Link to={link.to} title={link.title}>
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {useMobileView && (
                    <div className={styles.mobileNav} ref={mobileMenuRef}>
                        <button
                            className={styles.menuToggle}
                            title="Open or close the navigation menu"
                            onClick={() => setMobileMenuOpen((prev) => !prev)}
                            aria-label="Toggle navigation menu"
                        >
                            {mobileMenuOpen ? (
                                <FiX size={24} />
                            ) : (
                                <FiMenu size={24} />
                            )}
                        </button>
                        {mobileMenuOpen && (
                            <div
                                className={`${styles.dropdown} ${styles.mobileDropdown}`}
                            >
                                {navLinks.map((link) => (
                                    <button
                                        key={link.key}
                                        // for the tooltip
                                        title={link.title}
                                        className={styles.dropdownItem}
                                        onClick={() => {
                                            setMobileMenuOpen(false);
                                            navigate(link.to);
                                        }}
                                    >
                                        {link.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {token && (
                <div className={styles.headerRight}>
                    <div className={styles.profileMenu} ref={profileMenuRef}>
                        <button
                            className={styles.profileButton}
                            title="Open or close your profile menu to edit account or logout"
                            onClick={() => setShowDropdown((prev) => !prev)}
                        >
                            <FiUser size={20} />
                            <span className={styles.profileName}>
                                {firstName}
                            </span>
                        </button>
                        {showDropdown && (
                            <div className={styles.dropdown}>
                                <button
                                    className={styles.dropdownItem}
                                    title="Update your account details, such as name or email"
                                    onClick={() => {
                                        setShowDropdown(false);
                                        navigate('/edit-account');
                                    }}
                                >
                                    Edit Account
                                </button>
                                <button
                                    className={styles.dropdownItem}
                                    title="View instructions and download the mobile app"
                                    onClick={() => {
                                        setShowDropdown(false);
                                        navigate('/app');
                                    }}
                                >
                                    Download Mobile App
                                </button>
                                <button
                                    className={styles.dropdownItem}
                                    title="Read about the team behind MD-Sensei"
                                    onClick={() => {
                                        setShowDropdown(false);
                                        navigate('/about-team');
                                    }}
                                >
                                    About the Development Team
                                </button>

                                <button
                                    className={styles.dropdownItem}
                                    title="Sign out of your account securely"
                                    onClick={() => {
                                        setShowDropdown(false);
                                        logout();
                                    }}
                                >
                                    Logout
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Header;
